/*
 * TOTP setup — the same dialog for Add and for Update.
 *
 * Add and Update are the identical pair of calls; the service upserts on
 * (user, tenant, factor), which is what stops an update duplicating the
 * enrolment. What differs is the consequence, so the wording does too:
 *
 *   Add     — nothing to lose, straight to the QR.
 *   Update  — confirmSetup replaces the secret. The entry already in the user's
 *             authenticator stops working the moment it succeeds, and the call
 *             issues a new set of backup codes that voids the old set. That is
 *             worth a screen of its own before the QR appears; abandoning the
 *             dialog before entering a code changes nothing.
 *
 * beginSetup runs ONCE per attempt. A wrong code re-posts the same `secret` —
 * calling beginSetup again would rotate the secret out from under the QR the
 * user has just scanned, so a retry that "fixes itself" would in fact guarantee
 * failure forever.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Input, Modal, Spin } from "antd";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { logOutUser } from "../../../common";
import { dispatchTotpBeginSetup, dispatchTotpConfirmSetup } from "../../../utils/apiservice";
import { btn, Callout } from "./mfaCommon";
import BackupCodes from "./BackupCodes";

import copyText from "../../../utils/copyText";
// The service reports a wrong code as `{ message: "invalid totp" }`, which may
// arrive as a 400 or as a 200 with no `success`. Both mean "try again", and
// neither should surface the raw string.
const isInvalidCode = (msg) => /invalid\s*(totp|code|otp)/i.test(String(msg || ""));

export default function TotpSetupDialog({ open, mode, email, url, onClose, onEnrolled }) {
  const isUpdate = mode === "update";

  const [step, setStep] = useState("scan");     // "warn" | "scan" | "codes"
  const [setup, setSetup] = useState(null);     // beginSetup response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");       // beginSetup failed — no QR to show
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [codes, setCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  // One beginSetup per attempt, held in a ref so no state flip can re-enter it.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setStep(isUpdate ? "warn" : "scan");
    setSetup(null);
    setError("");
    setCode("");
    setCodeError("");
    setCodes([]);
    setCopied(false);
    setLoading(false);
    startedRef.current = false;
  }, [open, isUpdate]);

  const begin = useCallback(() => {
    startedRef.current = true;
    setLoading(true);
    setError("");
    const failed = (msg) => {
      setLoading(false);
      setError(msg);
      // Let "Try again" re-issue rather than dead-ending on a transient failure.
      startedRef.current = false;
    };
    dispatchTotpBeginSetup({
      payload: { email, url },
      onSuccess: (res) => {
        setLoading(false);
        if (!res?.secret) { failed("The server did not return a setup secret."); return; }
        setSetup(res);
      },
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { setLoading(false); logOutUser(); return; }
        failed(m || "Could not start authenticator setup.");
      },
      onError: ({ message: m }) => failed(m || "Could not start authenticator setup."),
    });
  }, [email, url]);

  useEffect(() => {
    if (!open || step !== "scan" || startedRef.current) return;
    begin();
  }, [open, step, begin]);

  const verify = () => {
    if (code.length !== 6 || verifying || !setup?.secret) return;
    setVerifying(true);
    setCodeError("");
    const failed = (msg) => {
      setVerifying(false);
      setCodeError(isInvalidCode(msg) ? "Invalid code, try again" : (msg || "Could not verify that code."));
    };
    dispatchTotpConfirmSetup({
      // The pending secret goes back with the code: the enrolment is committed
      // against this secret, not against whatever a later beginSetup issued.
      payload: { email, url, secret: setup.secret, code },
      onSuccess: (res) => {
        setVerifying(false);
        // A 200 is not by itself a success here — the invalid-code answer is
        // shaped like one. Only an explicit success (or codes to show) counts.
        if (res?.success !== true && !Array.isArray(res?.backup_codes)) {
          setCodeError(isInvalidCode(res?.message) ? "Invalid code, try again"
            : (res?.message || "Could not verify that code."));
          return;
        }
        const list = Array.isArray(res.backup_codes) ? res.backup_codes.filter(Boolean) : [];
        if (!list.length) {
          // Enrolled, but this call issued no codes. Nothing to gate the close
          // on, so finish rather than showing an empty "save these" panel.
          onEnrolled?.({ message: isUpdate ? "Authenticator app updated." : "Authenticator app added." });
          return;
        }
        setCodes(list);
        setStep("codes");
      },
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { setVerifying(false); logOutUser(); return; }
        failed(m);
      },
      onError: ({ message: m }) => failed(m),
    });
  };

  const copySecret = async () => {
    const text = setup?.manual_entry || setup?.secret;
    if (!text) return;
    try {
      await copyText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  // The codes screen owns its own dismissal (BackupCodes' acknowledgement), so
  // the mask and the X are taken away there — they are the two ways a user
  // loses codes that are never shown again.
  const gated = step === "codes";

  return (
    <Modal
      open={open}
      onCancel={gated ? undefined : onClose}
      footer={null}
      destroyOnClose
      maskClosable={!gated}
      closable={!gated}
      width={460}
      title={
        <span style={{ fontFamily: AN_FONT, fontSize: 15, fontWeight: 600, color: AN_COLORS.ink }}>
          {step === "codes"
            ? "Your recovery codes"
            : isUpdate ? "Update authenticator app" : "Add authenticator app"}
        </span>
      }
    >
      <div style={{ fontFamily: AN_FONT }}>
        {step === "warn" && (
          <>
            <Callout tone="warn" icon={<AlertTriangle size={15} color={AN_COLORS.warn} />}>
              Setting up your authenticator again issues a <strong style={{ fontWeight: 600 }}>new secret</strong>.
              When you finish, the entry already in your authenticator app stops working and your current
              recovery codes are replaced.
            </Callout>
            <div style={{ fontSize: 12.5, color: AN_COLORS.muted, lineHeight: 1.6, marginTop: 12 }}>
              Nothing changes until you enter a code from the new QR — you can close this dialog before
              then and keep using your current app.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button type="button" style={btn("secondary")} onClick={onClose}>Cancel</button>
              <button type="button" style={btn("primary")} onClick={() => setStep("scan")}>Re-scan QR</button>
            </div>
          </>
        )}

        {step === "scan" && loading && (
          <div style={{ padding: "48px 0", textAlign: "center" }}><Spin /></div>
        )}

        {step === "scan" && !loading && error && (
          <>
            <Callout tone="danger">{error}</Callout>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button type="button" style={btn("secondary")} onClick={onClose}>Cancel</button>
              <button type="button" style={btn("primary")} onClick={begin}>Try again</button>
            </div>
          </>
        )}

        {step === "scan" && !loading && !error && setup && (
          <>
            <div style={{ fontSize: 13, color: AN_COLORS.ink2, lineHeight: 1.6 }}>
              Scan this with your authenticator app, then enter the six-digit code it shows.
            </div>

            {setup.qr_code ? (
              <div style={{ display: "flex", justifyContent: "center", margin: "16px 0 12px" }}>
                <img
                  src={`data:image/png;base64,${setup.qr_code}`}
                  alt="Authenticator setup QR code"
                  width={176}
                  height={176}
                  style={{ border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, padding: 8, background: "#FFFFFF" }}
                />
              </div>
            ) : (
              <div style={{ margin: "14px 0 12px" }}>
                <Callout>No QR image was returned — use the setup key below instead.</Callout>
              </div>
            )}

            {/* Offered alongside the QR, not behind a disclosure: a user setting
                this up on the same device they are reading it on cannot scan. */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              padding: "9px 11px", background: AN_COLORS.bg,
              border: `1px solid ${AN_COLORS.line}`, borderRadius: 8,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontFamily: AN_MONO, fontSize: 10, letterSpacing: 1.1,
                  textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 3,
                }}>
                  Can&rsquo;t scan? Setup key
                </div>
                <div style={{
                  fontFamily: AN_MONO, fontSize: 12.5, color: AN_COLORS.ink,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", userSelect: "all",
                }}>
                  {setup.manual_entry || setup.secret}
                </div>
              </div>
              <button type="button" onClick={copySecret} style={{ ...btn("secondary"), flexShrink: 0 }}>
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2, marginBottom: 7 }}>
                Six-digit code
              </div>
              <Input
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setCodeError(""); }}
                onPressEnter={verify}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                status={codeError ? "error" : undefined}
                style={{ fontFamily: AN_MONO, letterSpacing: 3, fontSize: 15, width: 150 }}
              />
              {codeError && (
                <div style={{ fontSize: 12, color: AN_COLORS.danger, marginTop: 7 }}>{codeError}</div>
              )}
            </div>

            {isUpdate && (
              <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 12, lineHeight: 1.55 }}>
                Your current authenticator entry keeps working until this code is accepted.
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button type="button" style={btn("secondary")} onClick={onClose}>Cancel</button>
              <button
                type="button"
                onClick={verify}
                disabled={code.length !== 6 || verifying}
                style={{
                  ...btn("primary"),
                  opacity: code.length === 6 && !verifying ? 1 : 0.5,
                  cursor: code.length === 6 && !verifying ? "pointer" : "not-allowed",
                }}
              >
                {verifying ? "Verifying…" : "Verify and activate"}
              </button>
            </div>
          </>
        )}

        {step === "codes" && (
          <BackupCodes
            codes={codes}
            replaced={isUpdate}
            onDone={() => onEnrolled?.({
              message: isUpdate ? "Authenticator app updated." : "Authenticator app added.",
            })}
          />
        )}
      </div>
    </Modal>
  );
}
